"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createRiskAction,
  updateRiskAction,
  deleteRiskAction,
  createBlockerAction,
  updateBlockerAction,
  deleteBlockerAction,
  createDecisionAction,
  updateDecisionAction,
  deleteDecisionAction,
} from "@/actions/risk-actions";
import {
  AlertTriangle,
  Flame,
  CheckCircle2,
  AlertOctagon,
  Calendar,
  User,
  Plus,
  Edit2,
  Trash2,
  FileText,
  Activity,
  Milestone,
  CheckSquare,
  Search,
} from "lucide-react";

interface RiskItem {
  id: string;
  title: string;
  severity: "low" | "medium" | "high" | "critical";
  status: string;
  createdAt: string;
}

interface BlockerItem {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  reporterId: string | null;
  reporterName: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
}

interface DecisionItem {
  id: string;
  title: string;
  context: string | null;
  verificationDeadline: string;
  deltaResult: string | null;
  status: "pending" | "verified";
  createdAt: string;
  deciderId: string | null;
  deciderName: string | null;
  targetKpiId: string | null;
  targetKpiTitle: string | null;
  targetGoalId: string | null;
  targetGoalTitle: string | null;
}

interface AgentItem {
  id: string;
  name: string;
}

interface KpiItem {
  id: string;
  title: string;
}

interface GoalItem {
  id: string;
  title: string;
}

interface RisksClientProps {
  initialRisks: RiskItem[];
  initialBlockers: BlockerItem[];
  initialDecisions: DecisionItem[];
  agents: AgentItem[];
  kpis: KpiItem[];
  goals: GoalItem[];
}

export function RisksClient({
  initialRisks,
  initialBlockers,
  initialDecisions,
  agents,
  kpis,
  goals,
}: RisksClientProps) {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<
    "risks" | "blockers" | "decisions"
  >("risks");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modals state
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);
  const [isBlockerModalOpen, setIsBlockerModalOpen] = useState(false);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);

  // Edit / Action targets
  const [editingRisk, setEditingRisk] = useState<RiskItem | null>(null);
  const [editingBlocker, setEditingBlocker] = useState<BlockerItem | null>(
    null,
  );
  const [editingDecision, setEditingDecision] = useState<DecisionItem | null>(
    null,
  );

  // Form inputs
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Risk Inputs
  const [riskTitle, setRiskTitle] = useState("");
  const [riskSeverity, setRiskSeverity] = useState<
    "low" | "medium" | "high" | "critical"
  >("medium");
  const [riskStatus, setRiskStatus] = useState("open");

  // Blocker Inputs
  const [blockerTitle, setBlockerTitle] = useState("");
  const [blockerReporterId, setBlockerReporterId] = useState("");
  const [blockerAssigneeId, setBlockerAssigneeId] = useState("");
  const [blockerStatus, setBlockerStatus] = useState("open");

  // Decision Inputs
  const [decisionTitle, setDecisionTitle] = useState("");
  const [decisionDeciderId, setDecisionDeciderId] = useState("");
  const [decisionContext, setDecisionContext] = useState("");
  const [decisionTargetKpiId, setDecisionTargetKpiId] = useState("");
  const [decisionTargetGoalId, setDecisionTargetGoalId] = useState("");
  const [decisionDeadline, setDecisionDeadline] = useState("");
  const [decisionStatus, setDecisionStatus] = useState<"pending" | "verified">(
    "pending",
  );
  const [decisionDelta, setDecisionDelta] = useState("");

  // Clean form utility
  const resetForms = () => {
    setError("");
    setLoading(false);

    setRiskTitle("");
    setRiskSeverity("medium");
    setRiskStatus("open");
    setEditingRisk(null);

    setBlockerTitle("");
    setBlockerReporterId("");
    setBlockerAssigneeId("");
    setBlockerStatus("open");
    setEditingBlocker(null);

    setDecisionTitle("");
    setDecisionDeciderId("");
    setDecisionContext("");
    setDecisionTargetKpiId("");
    setDecisionTargetGoalId("");
    const defaultDeadline = new Date();
    defaultDeadline.setDate(defaultDeadline.getDate() + 30);
    setDecisionDeadline(defaultDeadline.toISOString().split("T")[0]);
    setDecisionStatus("pending");
    setDecisionDelta("");
    setEditingDecision(null);
  };

  const handleOpenRiskModal = (risk?: RiskItem) => {
    resetForms();
    if (risk) {
      setEditingRisk(risk);
      setRiskTitle(risk.title);
      setRiskSeverity(risk.severity);
      setRiskStatus(risk.status);
    }
    setIsRiskModalOpen(true);
  };

  const handleOpenBlockerModal = (blocker?: BlockerItem) => {
    resetForms();
    if (blocker) {
      setEditingBlocker(blocker);
      setBlockerTitle(blocker.title);
      setBlockerReporterId(blocker.reporterId || "");
      setBlockerAssigneeId(blocker.assigneeId || "");
      setBlockerStatus(blocker.status);
    }
    setIsBlockerModalOpen(true);
  };

  const handleOpenDecisionModal = (decision?: DecisionItem) => {
    resetForms();
    if (decision) {
      setEditingDecision(decision);
      setDecisionTitle(decision.title);
      setDecisionDeciderId(decision.deciderId || "");
      setDecisionContext(decision.context || "");
      setDecisionTargetKpiId(decision.targetKpiId || "");
      setDecisionTargetGoalId(decision.targetGoalId || "");
      setDecisionDeadline(decision.verificationDeadline.split("T")[0]);
      setDecisionStatus(decision.status);
      setDecisionDelta(decision.deltaResult || "");
    }
    setIsDecisionModalOpen(true);
  };

  // HANDLERS
  const handleSubmitRisk = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!riskTitle.trim()) {
      setError("Il titolo del rischio è obbligatorio.");
      return;
    }

    setLoading(true);
    try {
      let res;
      if (editingRisk) {
        res = await updateRiskAction(editingRisk.id, {
          title: riskTitle,
          severity: riskSeverity,
          status: riskStatus,
        });
      } else {
        res = await createRiskAction({
          title: riskTitle,
          severity: riskSeverity,
          status: riskStatus,
        });
      }

      if (res.error) {
        setError(res.error);
      } else {
        setIsRiskModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Si è verificato un errore.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRisk = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo rischio?")) return;
    try {
      const res = await deleteRiskAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione.");
    }
  };

  const handleSubmitBlocker = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!blockerTitle.trim()) {
      setError("Il titolo dell'ostacolo è obbligatorio.");
      return;
    }

    setLoading(true);
    try {
      let res;
      if (editingBlocker) {
        res = await updateBlockerAction(editingBlocker.id, {
          title: blockerTitle,
          reporterId: blockerReporterId || null,
          assigneeId: blockerAssigneeId || null,
          status: blockerStatus,
        });
      } else {
        res = await createBlockerAction({
          title: blockerTitle,
          reporterId: blockerReporterId || null,
          assigneeId: blockerAssigneeId || null,
          status: blockerStatus,
        });
      }

      if (res.error) {
        setError(res.error);
      } else {
        setIsBlockerModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Si è verificato un errore.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlocker = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo ostacolo?")) return;
    try {
      const res = await deleteBlockerAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione.");
    }
  };

  const handleSubmitDecision = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!decisionTitle.trim()) {
      setError("Il titolo della decisione è obbligatoria.");
      return;
    }
    if (!decisionDeadline) {
      setError("La scadenza per la verifica è obbligatoria.");
      return;
    }

    setLoading(true);
    try {
      let res;
      const deltaVal = decisionDelta ? parseFloat(decisionDelta) : null;

      if (editingDecision) {
        res = await updateDecisionAction(editingDecision.id, {
          title: decisionTitle,
          deciderId: decisionDeciderId || null,
          context: decisionContext || null,
          targetKpiId: decisionTargetKpiId || null,
          targetGoalId: decisionTargetGoalId || null,
          verificationDeadline: decisionDeadline,
          status: decisionStatus,
          deltaResult: isNaN(deltaVal as any) ? null : deltaVal,
        });
      } else {
        res = await createDecisionAction({
          title: decisionTitle,
          deciderId: decisionDeciderId || null,
          context: decisionContext || null,
          targetKpiId: decisionTargetKpiId || null,
          targetGoalId: decisionTargetGoalId || null,
          verificationDeadline: decisionDeadline,
          status: decisionStatus,
        });
      }

      if (res.error) {
        setError(res.error);
      } else {
        setIsDecisionModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Si è verificato un errore.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDecision = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questa decisione?")) return;
    try {
      const res = await deleteDecisionAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione.");
    }
  };

  // Helper date formatter
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Badges maps
  const getSeverityBadge = (sev: RiskItem["severity"]) => {
    switch (sev) {
      case "critical":
        return (
          <Badge variant="error" className="capitalize flex items-center gap-1">
            <Flame className="h-3 w-3" /> Critico
          </Badge>
        );
      case "high":
        return (
          <Badge variant="error" className="capitalize">
            Alto
          </Badge>
        );
      case "medium":
        return (
          <Badge variant="warning" className="capitalize">
            Medio
          </Badge>
        );
      case "low":
      default:
        return (
          <Badge variant="neutral" className="capitalize">
            Basso
          </Badge>
        );
    }
  };

  const getRiskStatusBadge = (status: string) => {
    return status === "mitigated" ? (
      <Badge variant="success">Mitigato</Badge>
    ) : (
      <Badge variant="error">Attivo</Badge>
    );
  };

  const getBlockerStatusBadge = (status: string) => {
    return status === "resolved" ? (
      <Badge variant="success">Risolto</Badge>
    ) : (
      <Badge variant="error">Bloccante</Badge>
    );
  };

  const getDecisionStatusBadge = (status: string) => {
    return status === "verified" ? (
      <Badge variant="success">Verificato</Badge>
    ) : (
      <Badge variant="warning">In verifica</Badge>
    );
  };

  // Filter lists based on tab and inputs
  const filteredRisks = initialRisks.filter((r) => {
    const matchSearch = r.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const filteredBlockers = initialBlockers.filter((b) => {
    const matchSearch = b.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === "all" || b.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const filteredDecisions = initialDecisions.filter((d) => {
    const matchSearch =
      d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (d.context &&
        d.context.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchStatus = statusFilter === "all" || d.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      {/* Navigation Tabs & Actions Box */}
      <div className="bg-card p-4 rounded-xl border border-border space-y-4">
        {/* Row 1: Tabs (Left) & Action Button (Right) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full">
          {/* Local Tab Switches */}
          <div className="flex items-center bg-card-muted/50 p-1 rounded-lg border border-border/50 self-start sm:self-auto">
            <button
              onClick={() => {
                setActiveTab("risks");
                setStatusFilter("all");
              }}
              className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                activeTab === "risks"
                  ? "bg-card text-primary-600 shadow-sm border border-border/30"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              Rischi ({initialRisks.length})
            </button>
            <button
              onClick={() => {
                setActiveTab("blockers");
                setStatusFilter("all");
              }}
              className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                activeTab === "blockers"
                  ? "bg-card text-primary-600 shadow-sm border border-border/30"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              Ostacoli ({initialBlockers.length})
            </button>
            <button
              onClick={() => {
                setActiveTab("decisions");
                setStatusFilter("all");
              }}
              className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                activeTab === "decisions"
                  ? "bg-card text-primary-600 shadow-sm border border-border/30"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              Decisioni ({initialDecisions.length})
            </button>
          </div>

          {/* Action Button */}
          <div className="self-end sm:self-auto shrink-0">
            {activeTab === "risks" && (
              <Button
                onClick={() => handleOpenRiskModal()}
                className="gap-1.5 h-9 text-xs py-1"
              >
                <Plus className="h-4 w-4" /> Nuovo Rischio
              </Button>
            )}
            {activeTab === "blockers" && (
              <Button
                onClick={() => handleOpenBlockerModal()}
                className="gap-1.5 h-9 text-xs py-1"
              >
                <Plus className="h-4 w-4" /> Nuovo Ostacolo
              </Button>
            )}
            {activeTab === "decisions" && (
              <Button
                onClick={() => handleOpenDecisionModal()}
                className="gap-1.5 h-9 text-xs py-1"
              >
                <Plus className="h-4 w-4" /> Nuova Decisione
              </Button>
            )}
          </div>
        </div>

        {/* Row 2: Search & Status Filters (Separated below) */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full border-t border-border/40 pt-3">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
            <Input
              placeholder={`Cerca ${
                activeTab === "risks"
                  ? "rischi"
                  : activeTab === "blockers"
                    ? "ostacoli"
                    : "decisioni"
              }...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 w-full"
            />
          </div>

          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-[160px] h-9"
          >
            <option value="all">Tutti gli stati</option>
            {activeTab === "risks" && (
              <>
                <option value="open">Aperto / Attivo</option>
                <option value="mitigated">Mitigato</option>
              </>
            )}
            {activeTab === "blockers" && (
              <>
                <option value="open">Bloccante</option>
                <option value="resolved">Risolto</option>
              </>
            )}
            {activeTab === "decisions" && (
              <>
                <option value="pending">In verifica</option>
                <option value="verified">Verificato</option>
              </>
            )}
          </Select>
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────────────────
         RISKS TAB VIEW
         ──────────────────────────────────────────────────────────────────────── */}
      {activeTab === "risks" && (
        <>
          {filteredRisks.length === 0 ? (
            <Card className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-xl bg-card/50 text-center max-w-xl mx-auto my-8">
              <AlertTriangle className="h-10 w-10 text-primary-500 mb-3 animate-pulse" />
              <h3 className="text-base font-bold text-text-primary">
                Nessun rischio trovato
              </h3>
              <p className="text-sm text-text-secondary mt-1">
                La tua organizzazione non ha registrato rischi per questa
                visualizzazione.
              </p>
              <Button
                onClick={() => handleOpenRiskModal()}
                className="mt-4 gap-1.5"
              >
                <Plus className="h-4 w-4" /> Registra Rischio
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredRisks.map((risk) => (
                <Card
                  key={risk.id}
                  onClick={() => handleOpenRiskModal(risk)}
                  className={`group relative flex flex-col justify-between hover:border-primary-500/50 transition-all border-l-4 cursor-pointer ${
                    risk.severity === "critical"
                      ? "border-l-error"
                      : risk.severity === "high"
                        ? "border-l-error/70"
                        : risk.severity === "medium"
                          ? "border-l-warning"
                          : "border-l-info"
                  }`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start gap-4">
                      <div className="space-y-1">
                        <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                          Valutazione del Rischio
                        </span>
                        <Link
                          href={`/explore?nodeId=${risk.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="block"
                        >
                          <CardTitle className="text-base font-bold leading-snug hover:text-primary-600 transition-colors">
                            {risk.title}
                          </CardTitle>
                        </Link>
                      </div>
                      <div
                        className="relative flex items-center gap-1.5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Badges container */}
                        <div className="flex items-center gap-1.5 transition-opacity group-hover:opacity-0 group-hover:pointer-events-none">
                          {getSeverityBadge(risk.severity)}
                          {getRiskStatusBadge(risk.status)}
                        </div>
                        {/* Hover actions container */}
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto">
                          <Link
                            href={`/explore?nodeId=${risk.id}`}
                            className="p-1 hover:bg-card-muted text-text-secondary hover:text-primary-600 rounded transition-colors"
                            title="Grafo"
                          >
                            <Activity className="h-3.5 w-3.5" />
                          </Link>
                          <button
                            onClick={() => handleOpenRiskModal(risk)}
                            className="p-1 hover:bg-card-muted text-text-secondary hover:text-foreground rounded transition-colors"
                            title="Modifica"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteRisk(risk.id)}
                            className="p-1 hover:bg-card-muted text-error hover:text-error/80 rounded transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="text-xs text-text-secondary pb-4">
                    Registrato il: <strong>{formatDate(risk.createdAt)}</strong>
                  </CardContent>
                  <CardFooter
                    onClick={(e) => e.stopPropagation()}
                    className="flex justify-between items-center bg-card-muted/30 px-6 py-3 border-t border-border/20 text-xs text-text-muted"
                  >
                    <span>ID: {risk.id}</span>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
         BLOCKERS TAB VIEW
         ──────────────────────────────────────────────────────────────────────── */}
      {activeTab === "blockers" && (
        <>
          {filteredBlockers.length === 0 ? (
            <Card className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-xl bg-card/50 text-center max-w-xl mx-auto my-8">
              <AlertOctagon className="h-10 w-10 text-primary-500 mb-3 animate-pulse" />
              <h3 className="text-base font-bold text-text-primary">
                Nessun ostacolo registrato
              </h3>
              <p className="text-sm text-text-secondary mt-1">
                Nessun blocco operativo attivo rilevato. Ottimo lavoro!
              </p>
              <Button
                onClick={() => handleOpenBlockerModal()}
                className="mt-4 gap-1.5"
              >
                <Plus className="h-4 w-4" /> Segnala Ostacolo
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredBlockers.map((blocker) => (
                <Card
                  key={blocker.id}
                  onClick={() => handleOpenBlockerModal(blocker)}
                  className="group relative flex flex-col justify-between hover:border-primary-500/50 transition-all border-l-4 border-l-error/80 cursor-pointer"
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start gap-4">
                      <div className="space-y-1">
                        <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                          Ostacolo Operativo
                        </span>
                        <Link
                          href={`/explore?nodeId=${blocker.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="block"
                        >
                          <CardTitle className="text-base font-bold leading-snug hover:text-primary-600 transition-colors">
                            {blocker.title}
                          </CardTitle>
                        </Link>
                      </div>
                      <div
                        className="relative flex items-center gap-1.5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Badge container */}
                        <div className="transition-opacity group-hover:opacity-0 group-hover:pointer-events-none">
                          {getBlockerStatusBadge(blocker.status)}
                        </div>
                        {/* Hover actions container */}
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto">
                          <Link
                            href={`/explore?nodeId=${blocker.id}`}
                            className="p-1 hover:bg-card-muted text-text-secondary hover:text-primary-600 rounded transition-colors"
                            title="Grafo"
                          >
                            <Activity className="h-3.5 w-3.5" />
                          </Link>
                          <button
                            onClick={() => handleOpenBlockerModal(blocker)}
                            className="p-1 hover:bg-card-muted text-text-secondary hover:text-foreground rounded transition-colors"
                            title="Modifica"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteBlocker(blocker.id)}
                            className="p-1 hover:bg-card-muted text-error hover:text-error/80 rounded transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 pb-4 text-xs">
                    <div className="grid grid-cols-2 gap-2 text-text-secondary">
                      <div className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5" />
                        {blocker.reporterId ? (
                          <Link
                            href={`/explore?nodeId=${blocker.reporterId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:text-primary-600 hover:underline transition-colors"
                          >
                            Reporter: <strong>{blocker.reporterName}</strong>
                          </Link>
                        ) : (
                          <span>
                            Reporter:{" "}
                            <strong>{blocker.reporterName || "N/A"}</strong>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5" />
                        {blocker.assigneeId ? (
                          <Link
                            href={`/explore?nodeId=${blocker.assigneeId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:text-primary-600 hover:underline transition-colors"
                          >
                            Assegnato: <strong>{blocker.assigneeName}</strong>
                          </Link>
                        ) : (
                          <span>
                            Assegnato:{" "}
                            <strong>
                              {blocker.assigneeName || "Non assegnato"}
                            </strong>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="border-t border-border/40 pt-2 text-text-muted">
                      Rilevato il:{" "}
                      <strong>{formatDate(blocker.createdAt)}</strong>
                    </div>
                  </CardContent>
                  <CardFooter
                    onClick={(e) => e.stopPropagation()}
                    className="flex justify-between items-center bg-card-muted/30 px-6 py-3 border-t border-border/20 text-xs text-text-muted"
                  >
                    <span>ID: {blocker.id}</span>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
         DECISIONS TAB VIEW
         ──────────────────────────────────────────────────────────────────────── */}
      {activeTab === "decisions" && (
        <>
          {filteredDecisions.length === 0 ? (
            <Card className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-xl bg-card/50 text-center max-w-xl mx-auto my-8">
              <CheckSquare className="h-10 w-10 text-primary-500 mb-3 animate-pulse" />
              <h3 className="text-base font-bold text-text-primary">
                Nessuna decisione registrata
              </h3>
              <p className="text-sm text-text-secondary mt-1">
                Traccia le decisioni strategiche per verificarne l&apos;impatto
                reale sui KPI aziendali.
              </p>
              <Button
                onClick={() => handleOpenDecisionModal()}
                className="mt-4 gap-1.5"
              >
                <Plus className="h-4 w-4" /> Registra Decisione
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {filteredDecisions.map((decision) => (
                <Card
                  key={decision.id}
                  onClick={() => handleOpenDecisionModal(decision)}
                  className="group relative flex flex-col justify-between hover:border-primary-500/50 transition-all border-l-4 border-l-primary-500 bg-card cursor-pointer"
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start gap-4">
                      <div className="space-y-1">
                        <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                          Decisione & Registro di Impatto
                        </span>
                        <Link
                          href={`/explore?nodeId=${decision.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="block"
                        >
                          <CardTitle className="text-base font-bold leading-snug hover:text-primary-600 transition-colors">
                            {decision.title}
                          </CardTitle>
                        </Link>
                      </div>
                      <div
                        className="relative flex items-center gap-1.5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Badge container */}
                        <div className="transition-opacity group-hover:opacity-0 group-hover:pointer-events-none">
                          {getDecisionStatusBadge(decision.status)}
                        </div>
                        {/* Hover actions container */}
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto">
                          <Link
                            href={`/explore?nodeId=${decision.id}`}
                            className="p-1 hover:bg-card-muted text-text-secondary hover:text-primary-600 rounded transition-colors"
                            title="Grafo"
                          >
                            <Activity className="h-3.5 w-3.5" />
                          </Link>
                          <button
                            onClick={() => handleOpenDecisionModal(decision)}
                            className="p-1 hover:bg-card-muted text-text-secondary hover:text-foreground rounded transition-colors"
                            title="Modifica"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteDecision(decision.id)}
                            className="p-1 hover:bg-card-muted text-error hover:text-error/80 rounded transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 pb-4">
                    {decision.context && (
                      <div className="p-3 bg-card-muted/50 border border-border/50 rounded-lg text-sm text-text-secondary italic">
                        &ldquo;{decision.context}&rdquo;
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-text-secondary">
                      <div className="space-y-1">
                        <span className="text-text-muted block uppercase tracking-wider text-[10px] font-bold">
                          Responsabile (Decider)
                        </span>
                        <div className="flex items-center gap-1.5 font-semibold text-text-primary">
                          <User className="h-3.5 w-3.5" />
                          {decision.deciderId ? (
                            <Link
                              href={`/explore?nodeId=${decision.deciderId}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-primary-600 hover:underline transition-colors"
                            >
                              {decision.deciderName}
                            </Link>
                          ) : (
                            <span>{decision.deciderName || "Nessuno"}</span>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1">
                        <span className="text-text-muted block uppercase tracking-wider text-[10px] font-bold">
                          Scadenza Verifica
                        </span>
                        <div className="flex items-center gap-1.5 font-semibold text-text-primary">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>
                            {formatDate(decision.verificationDeadline)}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <span className="text-text-muted block uppercase tracking-wider text-[10px] font-bold">
                          Impatto Misurato (Delta)
                        </span>
                        <div className="flex items-center gap-1.5 font-bold text-primary-600 text-sm">
                          <Activity className="h-3.5 w-3.5" />
                          <span>
                            {decision.deltaResult !== null
                              ? `${Number(decision.deltaResult) > 0 ? "+" : ""}${decision.deltaResult}`
                              : "N/D (In attesa)"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs border-t border-border/40 pt-3 text-text-secondary">
                      {decision.targetGoalId && (
                        <div>
                          Obiettivo Collegato:{" "}
                          <Link
                            href={`/explore?nodeId=${decision.targetGoalId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:text-primary-600 hover:underline transition-colors"
                          >
                            <strong className="text-text-primary">
                              {decision.targetGoalTitle}
                            </strong>
                          </Link>
                        </div>
                      )}
                      {decision.targetKpiId && (
                        <div>
                          KPI Collegato:{" "}
                          <Link
                            href={`/explore?nodeId=${decision.targetKpiId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:text-primary-600 hover:underline transition-colors"
                          >
                            <strong className="text-text-primary">
                              {decision.targetKpiTitle}
                            </strong>
                          </Link>
                        </div>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter
                    onClick={(e) => e.stopPropagation()}
                    className="flex justify-between items-center bg-card-muted/30 px-6 py-3 border-t border-border/20 text-xs text-text-muted"
                  >
                    <span>Creato il: {formatDate(decision.createdAt)}</span>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
         MODALS - RISK FORM DIALOG
         ──────────────────────────────────────────────────────────────────────── */}
      <Dialog open={isRiskModalOpen} onOpenChange={setIsRiskModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingRisk ? "Modifica Rischio" : "Registra Nuovo Rischio"}
            </DialogTitle>
            <DialogDescription>
              Aggiungi o aggiorna un rischio strategico e valuta il suo
              potenziale impatto.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitRisk} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="risk-title">Titolo del Rischio</Label>
              <Input
                id="risk-title"
                placeholder="es. Dipendenza eccessiva da un singolo fornitore cloud"
                value={riskTitle}
                onChange={(e) => setRiskTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="risk-severity">Gravità (Severity)</Label>
                <Select
                  id="risk-severity"
                  value={riskSeverity}
                  onChange={(e) => setRiskSeverity(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="low">Bassa (Low)</option>
                  <option value="medium">Media (Medium)</option>
                  <option value="high">Alta (High)</option>
                  <option value="critical">Critica (Critical)</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="risk-status">Stato</Label>
                <Select
                  id="risk-status"
                  value={riskStatus}
                  onChange={(e) => setRiskStatus(e.target.value)}
                  disabled={loading}
                >
                  <option value="open">Attivo / Aperto</option>
                  <option value="mitigated">Mitigato</option>
                </Select>
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsRiskModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingRisk ? "Salva Modifiche" : "Registra Rischio"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ────────────────────────────────────────────────────────────────────────
         MODALS - BLOCKER FORM DIALOG
         ──────────────────────────────────────────────────────────────────────── */}
      <Dialog open={isBlockerModalOpen} onOpenChange={setIsBlockerModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingBlocker ? "Modifica Ostacolo" : "Segnala Nuovo Ostacolo"}
            </DialogTitle>
            <DialogDescription>
              Segnala un blocco operativo che impedisce l&apos;avanzamento dei
              lavori.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitBlocker} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="blocker-title">Titolo Ostacolo</Label>
              <Input
                id="blocker-title"
                placeholder="es. Mancata approvazione credenziali sandbox gateway"
                value={blockerTitle}
                onChange={(e) => setBlockerTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="blocker-reporter">
                  Segnalato da (Reporter)
                </Label>
                <Select
                  id="blocker-reporter"
                  value={blockerReporterId}
                  onChange={(e) => setBlockerReporterId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Seleziona Agente...</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="blocker-assignee">Assegnato a (Assignee)</Label>
                <Select
                  id="blocker-assignee"
                  value={blockerAssigneeId}
                  onChange={(e) => setBlockerAssigneeId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Seleziona Agente...</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="blocker-status">Stato</Label>
              <Select
                id="blocker-status"
                value={blockerStatus}
                onChange={(e) => setBlockerStatus(e.target.value)}
                disabled={loading}
              >
                <option value="open">Bloccante</option>
                <option value="resolved">Risolto</option>
              </Select>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsBlockerModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingBlocker ? "Salva Modifiche" : "Segnala Ostacolo"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ────────────────────────────────────────────────────────────────────────
         MODALS - DECISION FORM DIALOG
         ──────────────────────────────────────────────────────────────────────── */}
      <Dialog open={isDecisionModalOpen} onOpenChange={setIsDecisionModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingDecision
                ? "Modifica Decisione"
                : "Registra Nuova Decisione"}
            </DialogTitle>
            <DialogDescription>
              Documenta una scelta strategica e pianifica la verifica del suo
              impatto.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitDecision} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="decision-title">Titolo Decisione</Label>
              <Input
                id="decision-title"
                placeholder="es. Migrazione a infrastruttura Serverless per le API"
                value={decisionTitle}
                onChange={(e) => setDecisionTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="decision-context">Contesto / Motivazione</Label>
              <textarea
                id="decision-context"
                placeholder="Spiega brevemente il motivo di questa decisione e i risultati attesi..."
                value={decisionContext}
                onChange={(e) => setDecisionContext(e.target.value)}
                disabled={loading}
                className="flex w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="decision-decider">Deciso da (Decider)</Label>
                <Select
                  id="decision-decider"
                  value={decisionDeciderId}
                  onChange={(e) => setDecisionDeciderId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Seleziona Agente...</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="decision-deadline">Scadenza Verifica</Label>
                <Input
                  id="decision-deadline"
                  type="date"
                  value={decisionDeadline}
                  onChange={(e) => setDecisionDeadline(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="decision-goal">Obiettivo Collegato</Label>
                <Select
                  id="decision-goal"
                  value={decisionTargetGoalId}
                  onChange={(e) => setDecisionTargetGoalId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Nessuno</option>
                  {goals.map((goal) => (
                    <option key={goal.id} value={goal.id}>
                      {goal.title}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="decision-kpi">KPI Collegato</Label>
                <Select
                  id="decision-kpi"
                  value={decisionTargetKpiId}
                  onChange={(e) => setDecisionTargetKpiId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Nessuno</option>
                  {kpis.map((kpi) => (
                    <option key={kpi.id} value={kpi.id}>
                      {kpi.title}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 border-t border-border/40 pt-3">
              <div className="space-y-1.5">
                <Label htmlFor="decision-status">Stato della Decisione</Label>
                <Select
                  id="decision-status"
                  value={decisionStatus}
                  onChange={(e) => setDecisionStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="pending">In verifica</option>
                  <option value="verified">Verificato</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="decision-delta">
                  Delta Misurato (Impatto KPI)
                </Label>
                <Input
                  id="decision-delta"
                  type="number"
                  step="0.01"
                  placeholder="es. +15.5 o -3.0"
                  value={decisionDelta}
                  onChange={(e) => setDecisionDelta(e.target.value)}
                  disabled={loading || decisionStatus !== "verified"}
                />
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsDecisionModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingDecision ? "Salva Modifiche" : "Registra Decisione"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
